import type { CoachDashboardStats } from '@/types';
import ConditionalLoadAlertsWidget from '@/components/dashboard/ConditionalLoadAlertsWidget';
import PerformanceTrendsWidget from '@/components/dashboard/PerformanceTrendsWidget';
import TrainingMetricsWidget from '@/components/dashboard/TrainingMetricsWidget';
import {
  DashboardHero,
  DashboardMetricStrip,
  DashboardSection,
  FixtureBrief,
  PlayerStatusList,
  ReportDigest,
} from '@/components/dashboard/primitives';

interface CoachDashboardViewProps {
  data: CoachDashboardStats;
}

export default function CoachDashboardView({ data }: CoachDashboardViewProps) {
  const developmentItems = data.performance.underPerformers.map((player) => ({
    id: `focus-${player.id}`,
    name: player.name,
    value: `${player.rating.toFixed(1)}/100`,
    status: 'Needs support',
    severity: 'warning' as const,
    to: `/players/${player.id}`,
  }));
  const progressItems = data.performance.formPlayers.map((player) => ({
    id: `progress-${player.id}`,
    name: player.name,
    value: `${player.rating.toFixed(1)}/100`,
    status: 'In form',
    severity: 'success' as const,
    to: `/players/${player.id}`,
  }));
  const performerItems = data.topPerformers.map((player) => ({
    id: player.id,
    name: player.name,
    value: `${player.rating.toFixed(1)}/100`,
    status: 'Rating',
    severity: 'success' as const,
    to: `/players/${player.id}`,
  }));

  return (
    <div className="space-y-6">
      <DashboardHero
        title="Training week pulse"
        supportingText={`${data.trainingMetrics.totalSessions} training sessions recorded this week.`}
        accent="coach"
        primaryValue={`${Math.round(data.trainingMetrics.attitudeScore)}/100`}
        primaryLabel="Attitude score"
      />

      <DashboardMetricStrip
        ariaLabel="Coach training metrics"
        metrics={[
          { label: 'Average fitness', value: `${Math.round(data.trainingMetrics.averageFitness)}/100` },
          { label: 'Weekly attendance', value: `${Math.round(data.trainingMetrics.weeklyAttendance)}%` },
          { label: 'Performance average', value: `${data.performance.averageRating.toFixed(1)}/100` },
          { label: 'Late arrivals', value: data.trainingMetrics.lateArrivals },
        ]}
      />

      <DashboardSection
        title="Player development focus"
        description="Players needing support and players building positive momentum."
      >
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="min-w-0">
            <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">Needs support</h3>
            <PlayerStatusList ariaLabel="Players needing development support" items={developmentItems} emptyTitle="No players currently need additional support" />
          </div>
          <div className="min-w-0">
            <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">Positive momentum</h3>
            <PlayerStatusList ariaLabel="Players making progress" items={progressItems} emptyTitle="No form players currently available" />
          </div>
        </div>
        <div className="mt-6">
          <ConditionalLoadAlertsWidget />
        </div>
      </DashboardSection>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <DashboardSection title="Training metrics" description="Attendance, fitness and training standards.">
          <TrainingMetricsWidget metrics={data.trainingMetrics} />
        </DashboardSection>
        <DashboardSection title="Performance trend" description="Current team and player form.">
          <PerformanceTrendsWidget metrics={data.performance} />
        </DashboardSection>
      </div>

      <DashboardSection title="Top performers" description="Current training and performance leaders.">
        <PlayerStatusList ariaLabel="Coach top performers" items={performerItems} emptyTitle="No performer data" />
      </DashboardSection>

      <DashboardSection title="Fixture brief" description="Context for upcoming match preparation.">
        <FixtureBrief fixtures={data.upcomingFixtures} />
      </DashboardSection>

      <DashboardSection title="Recent report digest" description="Latest staff observations for player development.">
        <ReportDigest reports={data.recentReportFeed} />
      </DashboardSection>
    </div>
  );
}
